import {
  CompactDateInput,
  CompactTextInput,
  DropdownList,
  FieldWrapper,
  FilterSearchableSelect,
  FilterSelect,
  FilterToggle,
  dateChipLabel,
} from "./jobsFilterUtils";

export const JOBS_STAFFING_SORT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "time", label: "Time" },
  { value: "request", label: "Request" },
  { value: "client", label: "Client" },
  { value: "contact", label: "Contact" },
];

export const JOBS_STAFFING_DEFAULT_FILTERS = {
  filterClient: "",
  clientSearch: "",
  filterLocation: "",
  locationSearch: "",
  filterFromDate: "",
  filterToDate: "",
  staffingShowDeleted: false,
  filterSortBy: "",
  filterSortDirection: "asc",
};

export function JobsStaffingRequestsFilterFields({
  focusedFieldId,
  onFocusClear,
  clients = [],
  locations = [],
  filterClient,
  clientSearch,
  filterLocation,
  locationSearch,
  filterFromDate,
  filterToDate,
  staffingShowDeleted,
  filterSortBy,
  filterSortDirection,
  showLocationDropdown,
  clientLoading = false,
  setClientSearch,
  setLocationSearch,
  setShowLocationDropdown,
  setFilterClient,
  setFilterLocation,
  setSelectedLocation,
  setLocations,
  getClientLocations,
  setFilterFromDate,
  setFilterToDate,
  setStaffingShowDeleted,
  setFilterSortBy,
  setFilterSortDirection,
}) {
  const clientOptions = (clients || []).map((client) => ({
    value: String(client.id),
    label: String(client.name || ""),
  }));

  const selectedClientLabel =
    clientOptions.find((o) => o.value === String(filterClient))?.label ||
    clientSearch ||
    "";

  const filteredLocations = locations.filter((l) =>
    String(l?.name || "").toLowerCase().includes(String(locationSearch || "").toLowerCase()),
  );

  return (
    <>
      <FieldWrapper fieldId="client" label="Client" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSearchableSelect
          id="jobs-staffing-client"
          value={filterClient || ""}
          selectedLabel={selectedClientLabel}
          loading={clientLoading}
          onQueryChange={setClientSearch}
          onChange={(clientId) => {
            if (!clientId) {
              setFilterClient("");
              setClientSearch("");
              setFilterLocation("");
              setLocationSearch("");
              setLocations([]);
              setShowLocationDropdown(false);
              return;
            }
            const client = (clients || []).find((c) => String(c.id) === String(clientId));
            setFilterClient(String(clientId));
            setClientSearch(client?.name || "");
            setSelectedLocation(null);
            setFilterLocation("");
            setLocationSearch("");
            setLocations([]);
            setShowLocationDropdown(false);
            getClientLocations(clientId, "STAFFINGREQUESTS");
          }}
          options={clientOptions}
          placeholder="Search clients..."
        />
      </FieldWrapper>

      <FieldWrapper fieldId="location" label="Client Location" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <div className="relative">
          <CompactTextInput
            id="jobs-staffing-location"
            value={locationSearch}
            disabled={!filterClient}
            placeholder={filterClient ? "Search location..." : "Select client first..."}
            onChange={(value) => {
              setLocationSearch(value);
              setShowLocationDropdown(true);
            }}
            onFocus={() => {
              if (filterClient) {
                setShowLocationDropdown(true);
              }
            }}
          />
          {filterLocation ? (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600"
              onClick={() => {
                setSelectedLocation(null);
                setFilterLocation("");
                setLocationSearch("");
                setShowLocationDropdown(false);
              }}
              aria-label="Clear location"
            >
              x
            </button>
          ) : null}
          {showLocationDropdown && filterClient ? (
            <DropdownList
              items={filteredLocations}
              emptyLabel="No locations found"
              onSelect={(loc) => {
                setSelectedLocation(loc);
                setLocationSearch(loc.name);
                setShowLocationDropdown(false);
                setFilterLocation(String(loc.id));
              }}
            />
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper fieldId="fromDate" label="From Date" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <CompactDateInput
          id="jobs-staffing-from"
          value={filterFromDate}
          onChange={(newFromDate) => {
            setFilterFromDate(newFromDate);
            if (newFromDate && filterToDate && newFromDate > filterToDate) {
              setFilterToDate("");
            }
          }}
        />
      </FieldWrapper>
      <FieldWrapper fieldId="toDate" label="To Date" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <CompactDateInput id="jobs-staffing-to" value={filterToDate} onChange={setFilterToDate} min={filterFromDate} />
      </FieldWrapper>
      <FieldWrapper fieldId="showDeleted" label="" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterToggle id="jobs-staffing-deleted" label="Show Deleted" checked={!!staffingShowDeleted} onChange={setStaffingShowDeleted} />
      </FieldWrapper>
      <FilterSelect id="jobs-staffing-sort-by" label="Sort By" value={filterSortBy || "all"} onChange={setFilterSortBy} options={JOBS_STAFFING_SORT_OPTIONS} placeholder={null} />
      <FilterSelect id="jobs-staffing-sort-direction" label="Sort Direction" value={filterSortDirection || "asc"} onChange={setFilterSortDirection} options={[{ value: "asc", label: "Ascending" }, { value: "desc", label: "Descending" }]} placeholder={null} />
    </>
  );
}

export function buildJobsStaffingRequestsFilterChips({
  searchTerm,
  filterClient,
  clientSearch,
  filterClientList = [],
  filterLocation,
  filterLocationList = [],
  filterFromDate,
  filterToDate,
  staffingShowDeleted,
}) {
  const chips = [];
  if (searchTerm?.trim()) chips.push({ id: "staffing-search", key: "search", fieldId: "search", label: `Search: ${searchTerm.trim()}` });
  if (filterClient) {
    const selectedClient = filterClientList.find((c) => String(c.id) === String(filterClient));
    chips.push({ id: "staffing-client", key: "client", fieldId: "client", label: `Client: ${selectedClient?.name || clientSearch || `#${filterClient}`}` });
  }
  if (filterLocation) {
    const selectedLocation = filterLocationList.find((l) => String(l.id) === String(filterLocation));
    chips.push({ id: "staffing-location", key: "location", fieldId: "location", label: `Location: ${selectedLocation?.name || `#${filterLocation}`}` });
  }
  if (filterFromDate) chips.push({ id: "staffing-from", key: "fromDate", fieldId: "fromDate", label: `From: ${dateChipLabel(filterFromDate)}` });
  if (filterToDate) chips.push({ id: "staffing-to", key: "toDate", fieldId: "toDate", label: `To: ${dateChipLabel(filterToDate)}` });
  if (staffingShowDeleted) chips.push({ id: "staffing-deleted", key: "showDeleted", fieldId: "showDeleted", label: "Show Deleted" });
  return chips;
}
